import { isTerminalBenchmarkStatus } from '../Assistant/assistantBenchmarkView';

import type { AssistantBenchmarkRunStatus } from '../../__generated__/globalTypes';

/**
 * Pure logic for the Releases scene's announce-button state machine.
 *
 * The button has three modes — co-located here so they can be unit-tested
 * without rendering the scene (vitest in mops-web runs in `node`, not jsdom).
 *
 * - `announce`     — no MOPS draft is open; the operator can announce the
 *                    pre-release at the previewed head SHA.
 * - `repost-slack` — a MOPS draft is already open at the same SHA; clicking
 *                    re-runs the announce mutation, which the server treats
 *                    as idempotent (no second draft, but Slack re-posts).
 * - `disabled`     — a MOPS draft is open at a different SHA. The operator
 *                    must publish or discard the existing draft on github.com
 *                    before they can announce a fresh one.
 */
export type AnnounceButtonMode = 'announce' | 'repost-slack' | 'disabled';

export interface IAnnounceButtonState {
  mode: AnnounceButtonMode;
  label: string;
  /** Tooltip shown on the disabled state, naming the conflicting SHA. */
  tooltip?: string;
}

interface IDeriveAnnounceButtonStateInput {
  expectedHeadSha: string;
  draftTargetSha: string | null;
  /**
   * Slack channel name (with leading `#`) the announce mutation will post to,
   * threaded through here from the caller because mops-api gates the channel
   * on `IS_DEV` (`#production-deploys-test` in dev, `#production-deploys`
   * elsewhere) — keeping the gate in mops-api means the frontend never lies
   * about where the post is going. Plumbed as a parameter (rather than
   * imported from `env.ts`) so this module stays pure and trivially testable.
   */
  slackChannel: string;
}

export const SHORT_SHA_LENGTH = 7;
const shortSha = (sha: string) => sha.slice(0, SHORT_SHA_LENGTH);

/**
 * Bookkeeping for the "Posted at HH:MM — view in Slack" confirmation line
 * surfaced after a successful announce or re-post. `slackUrl` is null when
 * Slack was unreachable; the announce flow degrades to "draft created, no
 * permalink" rather than failing the whole mutation.
 */
export interface IAnnounceJustPosted {
  postedAt: Date;
  slackUrl: string | null;
}

/**
 * Compute the announce button's mode + label + tooltip from the preview's
 * head SHA and the open draft's target SHA (or `null` when no draft exists).
 */
export function deriveAnnounceButtonState({
  expectedHeadSha,
  draftTargetSha,
  slackChannel,
}: IDeriveAnnounceButtonStateInput): IAnnounceButtonState {
  const announceLabel = `Announce pre-release in ${slackChannel}`;
  if (draftTargetSha === null) {
    return {
      mode: 'announce',
      label: announceLabel,
    };
  }
  if (draftTargetSha === expectedHeadSha) {
    return {
      mode: 'repost-slack',
      label: 'Re-post Slack',
    };
  }
  return {
    mode: 'disabled',
    label: announceLabel,
    tooltip: `A draft for a different SHA (${shortSha(draftTargetSha)}) is already open — view it above.`,
  };
}

/**
 * Format a Date as `HH:MM` (24-hour) in the operator's local timezone — used
 * for the "Posted at HH:MM — view in Slack" confirmation line after announce.
 * Pinned to 24-hour so the inline string stays compact across locales (no
 * trailing AM/PM).
 */
export function formatPostedAtClockTime(timestamp: Date): string {
  return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * First non-empty line of a multi-line release body — used as the truncated
 * summary in the history table. Returns an empty string when the body has
 * no non-blank lines.
 */
export function summaryFirstLine(body: string): string {
  for (const line of body.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
}

/**
 * Maps server error codes thrown by `adminPublishReleaseDraft` and
 * `adminDeleteReleaseDraft` to operator-facing toast messages. The
 * server throws `Error('CODE: human description …')` per
 * `ReleasesController` — we match the prefix and pick a tight,
 * action-oriented message.
 *
 * Falls back to the raw error message for codes we don't recognize so
 * a forward-compat server change doesn't strand the operator with
 * "Something went wrong".
 */
export function mapDraftMutationError(message: string): string {
  if (message.includes('NO_DRAFT_TO_PUBLISH')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('NO_DRAFT_TO_DELETE')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('MULTIPLE_DRAFTS_FOUND')) {
    return 'Multiple drafts exist (race condition). Use Discard until one remains, then publish.';
  }
  if (message.includes('STALE_DRAFT_TAG')) {
    return 'Draft changed — refresh.';
  }
  if (message.includes('CI_NOT_GREEN')) {
    return 'CI must be green to publish.';
  }
  if (message.includes('RELEASE_TAG_RACE')) {
    return 'Tag was just created externally — refresh.';
  }
  if (message.includes('GITHUB_PUBLISH_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('GITHUB_DELETE_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('GITHUB_UPDATE_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('NO_DRAFT_TO_UPDATE')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('STALE_CHECKLIST')) {
    return 'Checklist changed — refresh.';
  }
  return message;
}

/** True when the server error message indicates `MULTIPLE_DRAFTS_FOUND`. */
export function isMultipleDraftsError(message: string): boolean {
  return message.includes('MULTIPLE_DRAFTS_FOUND');
}

export const CHECKLIST_PREREQ_PREFIX = 'p:';
export const CHECKLIST_TEST_PLAN_PREFIX = 't:';

/**
 * Polling cadence for `adminGetCurrentReleaseDraft` — drives how quickly
 * one release manager sees another's checkbox toggles. Cheap (one GitHub
 * `listReleases` round-trip server-side); 30s feels current without
 * burning tokens. Tradeoff is documented in the persistence design doc.
 */
export const RELEASE_DRAFT_POLL_INTERVAL_MS = 30_000;

/**
 * Hex-encoded SHA-256 of the trimmed input. Server-side validation calls
 * `node:crypto` with the same algorithm; output is identical for
 * identical inputs. The toggle mutation sends this hash so the server
 * can detect that the client and the body agree on what the i-th
 * checklist line says.
 */
export async function sha256Hex(input: string): Promise<string> {
  const trimmed = input.trim();
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    /* istanbul ignore next */
    throw new Error('Web Crypto unavailable — cannot hash checklist line.');
  }
  const buffer = new TextEncoder().encode(trimmed);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

export interface IReleaseChecklistGateState {
  /** True when every item in both sections is checked. Always true when both lists are empty. */
  allChecked: boolean;
  /** Total number of items across both lists. Zero when both are empty. */
  totalCount: number;
  /** Number of items currently checked. */
  checkedCount: number;
  /** Total prerequisites (whether checked or not). */
  prereqTotalCount: number;
  /** Prerequisites still requiring review. */
  prereqUncheckedCount: number;
  /** Total test plan items (whether checked or not). */
  testPlanTotalCount: number;
  /** Test plan items still requiring completion. */
  testPlanUncheckedCount: number;
}

/** Stable key for a checklist item — `p:0`, `t:3`, etc. */
export function checklistKey(section: 'prereq' | 'testPlan', index: number): string {
  return `${section === 'prereq' ? CHECKLIST_PREREQ_PREFIX : CHECKLIST_TEST_PLAN_PREFIX}${index}`;
}

/**
 * Format a `Checked by **name** at M/D/YYYY @ H:MMam` marker for the
 * gate's optimistic overlay. Mirrors the server's `formatMarker` byte-for-
 * byte (Pacific Time, lowercase am/pm, zero-padded minute, no space
 * before am/pm) so the optimistic value is indistinguishable from what
 * the mutation response will write moments later — no flicker on
 * reconcile. The PT pin matches the release tool's audience (SF-based
 * release managers) and prevents the operator's browser zone from
 * showing one time while the server-written marker shows another.
 */
const PT_MARKER_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
export function formatChecklistMarker(operatorLocalPart: string, now: Date): string {
  const parts = PT_MARKER_FORMATTER.formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  const ampm = get('dayPeriod').toLowerCase();
  return `Checked by **${operatorLocalPart}** at ${get('month')}/${get('day')}/${get('year')} @ ${get('hour')}:${get('minute')}${ampm}`;
}

/**
 * Strips the LLM-generated `## Prerequisites` and
 * `## Pre-release test plan` sections from a release body. These
 * sections are surfaced as the interactive `ReleaseChecklistGate` when
 * a draft is open, so the static GitHub-style checkboxes inside the
 * body would be a visual duplicate.
 *
 * Each section runs from its `##` heading up to (but not including)
 * the next `##` heading or end of body. The trailing audit-footer
 * line stays intact because it isn't a `## …` heading. Heading text
 * is matched case-insensitively to tolerate minor LLM drift.
 *
 * Returns the body unchanged when neither section is present.
 */
export function stripChecklistSectionsFromBody(body: string): string {
  const sectionsToStripLower = new Set(['prerequisites', 'pre-release test plan']);
  const lines = body.split('\n');
  const kept: string[] = [];
  let skipping = false;

  for (const line of lines) {
    const headingMatch = /^##\s+(.+?)\s*$/.exec(line);
    if (headingMatch) {
      // New section starts — decide whether to skip it. The decision
      // re-evaluates on every `## …`, so consecutive stripped sections
      // and stripped→kept transitions both work.
      skipping = sectionsToStripLower.has(headingMatch[1].toLowerCase());
      if (skipping) {
        continue;
      }
    }
    if (!skipping) {
      kept.push(line);
    }
  }

  // Collapse any 3+ consecutive newlines left by the strip back down to two —
  // keeps the surrounding section spacing visually consistent.
  return kept.join('\n').replace(/\n{3,}/g, '\n\n');
}

/**
 * Pure aggregate state of the release checklist gate, given the source
 * lists and the set of currently-checked keys. Pulled out of
 * `ReleaseChecklistGate` so we can unit-test the state machine without
 * a DOM (the mops-web vitest config runs in `node`, not jsdom).
 *
 * Returns per-section uncheck counts so the disabled-Publish tooltip
 * can summarise progress as "x of y tests / a of b prerequisites" —
 * a more scannable signal than naming the next single item, which
 * scrolls past quickly in long releases.
 */
export function computeReleaseChecklistGateState(
  prereqTotalCount: number,
  testPlanTotalCount: number,
  checked: ReadonlySet<string>
): IReleaseChecklistGateState {
  let prereqCheckedCount = 0;
  for (let i = 0; i < prereqTotalCount; i += 1) {
    if (checked.has(checklistKey('prereq', i))) {
      prereqCheckedCount += 1;
    }
  }
  let testPlanCheckedCount = 0;
  for (let i = 0; i < testPlanTotalCount; i += 1) {
    if (checked.has(checklistKey('testPlan', i))) {
      testPlanCheckedCount += 1;
    }
  }

  const prereqUncheckedCount = prereqTotalCount - prereqCheckedCount;
  const testPlanUncheckedCount = testPlanTotalCount - testPlanCheckedCount;

  return {
    allChecked: prereqUncheckedCount === 0 && testPlanUncheckedCount === 0,
    totalCount: prereqTotalCount + testPlanTotalCount,
    checkedCount: prereqCheckedCount + testPlanCheckedCount,
    prereqTotalCount,
    prereqUncheckedCount,
    testPlanTotalCount,
    testPlanUncheckedCount,
  };
}

/**
 * Coverage state of the release's assistant benchmark, as the Publish gate sees it.
 *
 * `settled` is any terminal status — passed, failed, inconclusive, or errored.
 * The verdict is advisory, so a bad one still releases the button; what the gate
 * waits on is the run *finishing*, and `absent` covers the case where announcing
 * never managed to dispatch one.
 */
export type ReleaseBenchmarkGate = 'settled' | 'in-flight' | 'absent' | 'loading' | 'unavailable';

/**
 * Collapse the draft's benchmark run and its fetch state into the gate.
 *
 * `isLoading` and `hasError` are consulted only when no run has arrived: a poll
 * flips loading true again on a run already in hand, and regressing to a blocking
 * state there would yank Publish away from an operator mid-click.
 *
 * `unavailable` exists so a failed read is not mistaken for a missing run. Both
 * block, but only one of them means "go dispatch one" — an errored query nulls the
 * data, so without the distinction a network blip would send the operator off to
 * re-run an eval that is very likely already finished.
 */
export function releaseBenchmarkGate(
  run: { status: AssistantBenchmarkRunStatus } | null,
  isLoading: boolean,
  hasError: boolean
): ReleaseBenchmarkGate {
  if (run !== null) {
    return isTerminalBenchmarkStatus(run.status) ? 'settled' : 'in-flight';
  }
  if (hasError) {
    return 'unavailable';
  }
  return isLoading ? 'loading' : 'absent';
}

/**
 * The fields of a changelog commit the pin needs to name itself. Declared
 * structurally rather than imported from the generated query types so this
 * module stays pure and both the changelog and preview commit shapes satisfy it.
 */
export interface IReleasePinCommit {
  sha: string;
  shortSha: string;
  subject: string;
  prNumber?: number | null;
  prUrl?: string | null;
}

export interface IStartDraftButtonState {
  isEnabled: boolean;
  /** Names the blocker; empty when enabled. */
  tooltip: string;
  /**
   * Commit the draft would be cut at — the newest one whose CI passed, which
   * is `main` HEAD only when HEAD itself is green. Null when nothing on the
   * changelog is releasable, which is also the only case where the button is
   * blocked on CI at all.
   */
  pinnedSha: string | null;
  /**
   * The pinned commit itself, for naming it to the operator. Separate from
   * `pinnedSha` because the sha is what the mutation acts on and stays
   * authoritative even in the one case this is null — a pin and a commit list
   * that arrived from different fetches.
   */
  pinnedCommit: IReleasePinCommit | null;
  /** Commits newer than the pin, left out of the release. Zero when the pin is HEAD. */
  excludedCommitCount: number;
}

interface IDeriveStartDraftButtonStateInput {
  changeLogLoading: boolean;
  /** The changelog query resolved without data — GitHub was unreachable. */
  changeLogUnavailable: boolean;
  /** `success` / `failure` / `pending` / `unknown` from `summarizeCommitCi`. */
  ciConclusion: string;
  /** Commits since the base tag, oldest first — GitHub's `/compare` order. */
  commits: IReleasePinCommit[];
  /** `ReleaseChangeLog.releasableHeadSha` — newest green commit, or null. */
  releasableHeadSha: string | null;
}

/**
 * Whether the operator may start a draft release, which commit it would be cut
 * at, and why not when they can't.
 *
 * A red or still-running HEAD no longer blocks the button: the release pins
 * back to the newest green commit and leaves the rest for the next one. Only an
 * entirely ungreen changelog blocks, and each CI conclusion gets its own copy
 * there — `pending` and `unknown` are not failures, and telling an operator to
 * fix a red build when the build is merely still running sends them somewhere
 * pointless. Loading and unavailable are likewise distinct: the changelog is
 * null in both, but only one of them is worth waiting out.
 */
export function deriveStartDraftButtonState({
  changeLogLoading,
  changeLogUnavailable,
  ciConclusion,
  commits,
  releasableHeadSha,
}: IDeriveStartDraftButtonStateInput): IStartDraftButtonState {
  const blocked = (tooltip: string): IStartDraftButtonState => ({
    isEnabled: false,
    tooltip,
    pinnedSha: null,
    pinnedCommit: null,
    excludedCommitCount: 0,
  });
  if (changeLogLoading) {
    return blocked('Checking CI at head…');
  }
  if (changeLogUnavailable) {
    return blocked("Couldn't reach GitHub to check CI — retrying.");
  }
  if (commits.length === 0) {
    return blocked('No commits since the last release.');
  }
  if (releasableHeadSha === null) {
    if (ciConclusion === 'pending') {
      return blocked('CI is still running at head and no earlier commit has passed yet.');
    }
    if (ciConclusion === 'failure') {
      return blocked('CI is failing at head and no earlier commit has passed.');
    }
    return blocked('No CI checks found at head.');
  }
  const pinIndex = commits.findIndex((commit) => commit.sha === releasableHeadSha);
  return {
    isEnabled: true,
    tooltip: '',
    pinnedSha: releasableHeadSha,
    // A pin absent from the list is only reachable if the two fields came from
    // different fetches; naming no commit and excluding none is the honest
    // answer there, and the sha still drives the mutation.
    pinnedCommit: pinIndex === -1 ? null : commits[pinIndex],
    excludedCommitCount: pinIndex === -1 ? 0 : commits.length - 1 - pinIndex,
  };
}

interface IShouldAutoRunPreviewInput {
  /** Target SHA of the open draft, or null when none is open. */
  draftTargetSha: string | null;
  /** Target SHA of the draft this session published, or null. */
  publishedTargetSha: string | null;
  /** Whether a preview run has already been started for the current pin. */
  previewCalled: boolean;
}

/**
 * Whether the scene should kick off preview generation for the open draft on its
 * own. The draft card's checklist gate needs the structured prereqs and test plan,
 * so an operator arriving at an already-announced draft gets them without asking.
 *
 * The published-SHA exclusion is the non-obvious half. Publishing promotes the
 * draft's own commit to the *next* release's compare base, so a run at it is
 * rejected server-side — and publishing also clears the preview, which re-arms
 * this trigger while the draft query still answers with the draft that just
 * shipped. Without the exclusion every publish fires one doomed run.
 */
export function shouldAutoRunPreview({
  draftTargetSha,
  publishedTargetSha,
  previewCalled,
}: IShouldAutoRunPreviewInput): boolean {
  if (draftTargetSha === null || previewCalled) {
    return false;
  }
  return draftTargetSha !== publishedTargetSha;
}

export type PublishButtonDisabledReason =
  | 'ci-not-green'
  | 'checklist-incomplete'
  | 'benchmark-pending'
  | 'multiple-drafts'
  | 'preview-loading'
  | 'preview-error';

export interface IPublishButtonState {
  isEnabled: boolean;
  /**
   * Reason for the disabled state, when not enabled. Drives the tooltip
   * — `null` when the button is enabled (no tooltip needed). Order of
   * precedence: preview-error → preview-loading → multiple-drafts →
   * ci-not-green → checklist-incomplete → benchmark-pending. The benchmark
   * comes last on purpose: only one reason is shown, and work the operator
   * can act on should surface ahead of a wait they can only sit through.
   */
  disabledReason: PublishButtonDisabledReason | null;
  /**
   * Human-readable tooltip for the disabled state — names the next
   * blocker so the operator knows what to fix. Empty string when the
   * button is enabled.
   */
  tooltip: string;
}

interface IDerivePublishButtonStateInput {
  ciConclusion: string;
  allChecklistItemsChecked: boolean;
  prereqUncheckedCount: number;
  testPlanUncheckedCount: number;
  multipleDraftsDetected: boolean;
  previewLoading: boolean;
  previewError: boolean;
  benchmarkGate: ReleaseBenchmarkGate;
}

/**
 * Computes the Publish button's enable/disable + tooltip state from the
 * full set of preconditions: CI green, checklist complete, no race
 * condition, preview data loaded. The tooltip names the most-blocking
 * issue so the operator knows exactly what to fix.
 */
export function derivePublishButtonState({
  ciConclusion,
  allChecklistItemsChecked,
  prereqUncheckedCount,
  testPlanUncheckedCount,
  multipleDraftsDetected,
  previewLoading,
  previewError,
  benchmarkGate,
}: IDerivePublishButtonStateInput): IPublishButtonState {
  if (previewError) {
    return {
      isEnabled: false,
      disabledReason: 'preview-error',
      tooltip: 'Preview failed to load — refresh.',
    };
  }
  if (previewLoading) {
    return {
      isEnabled: false,
      disabledReason: 'preview-loading',
      tooltip: 'Loading checklist…',
    };
  }
  if (multipleDraftsDetected) {
    return {
      isEnabled: false,
      disabledReason: 'multiple-drafts',
      tooltip: 'Multiple drafts exist — use Discard until one remains.',
    };
  }
  if (ciConclusion !== 'success') {
    return {
      isEnabled: false,
      disabledReason: 'ci-not-green',
      tooltip: 'CI must be green to publish.',
    };
  }
  if (!allChecklistItemsChecked) {
    // Summary form: "{x} tests require completion\n{y} prerequisites require
    // review". Each line appears only when that section has unchecked items
    // — a release with no prereqs shouldn't dangle a "0 prerequisites" line,
    // and a release where every prereq is checked drops to a single tests
    // line. Both lines being skipped is unreachable here:
    // `allChecklistItemsChecked` is false iff at least one uncheckedCount > 0.
    const lines: string[] = [];
    if (testPlanUncheckedCount > 0) {
      lines.push(`${testPlanUncheckedCount} tests require completion`);
    }
    if (prereqUncheckedCount > 0) {
      lines.push(`${prereqUncheckedCount} prerequisites require review`);
    }
    return {
      isEnabled: false,
      disabledReason: 'checklist-incomplete',
      tooltip: lines.join('\n'),
    };
  }
  if (benchmarkGate !== 'settled') {
    return {
      isEnabled: false,
      disabledReason: 'benchmark-pending',
      tooltip:
        benchmarkGate === 'absent'
          ? 'No assistant benchmark for this commit — dispatch one from Assistant → Benchmarks using the target SHA.'
          : benchmarkGate === 'unavailable'
            ? "Couldn't read the assistant benchmark — retrying."
            : 'Assistant benchmark is still running — Publish waits for it.',
    };
  }
  return {
    isEnabled: true,
    disabledReason: null,
    tooltip: '',
  };
}
